// Shared number/amount formatting, locale-aware.
//
// Amounts follow the active UI language (i18n.language), so the same value
// renders as 7,800,163.00 in English, 7.800.163,00 in Italian/Spanish and
// 7 800 163,00 in Portuguese. Read the locale at call time so a language switch
// is reflected on the next render.

import i18n from '../i18n'

// Map our 2-letter app languages to full BCP-47 locales with the desired
// grouping/decimal conventions.
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  it: 'it-IT',
  es: 'es-ES',
  pt: 'pt-PT',
}

function currentLocale(): string {
  const lng = (i18n.language || 'en').slice(0, 2).toLowerCase()
  return LOCALE_MAP[lng] ?? 'en-US'
}

/** Money/amount with fixed decimals (default 2) and locale grouping. */
export function formatAmount(value: number, fractionDigits = 2): string {
  const v = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat(currentLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(v)
}

/** Integer/count with locale grouping and no forced decimals. */
export function formatNumber(value: number, maximumFractionDigits = 0): string {
  const v = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits }).format(v)
}

/**
 * Money with the proper currency symbol AND locale grouping/placement, e.g.
 * "€ 7.800.163,00" (it) vs "$7,800,163.00" (en). Use this when you have the
 * ISO currency code; for a pre-resolved symbol use formatAmount + the symbol.
 */
export function formatCurrency(value: number, currencyCode: string): string {
  const v = Number.isFinite(value) ? value : 0
  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(v)
  } catch {
    // Unknown/invalid ISO code → fall back to grouped amount + raw code.
    return `${currencyCode} ${formatAmount(v)}`
  }
}
